import React, { useMemo, useState } from "react";
import {
  ArrayInput,
  BooleanInput,
  FormDataConsumer,
  FormTab,
  Record,
  required,
  SelectInput,
  SimpleFormIterator,
  TabbedForm,
  TextInput,
  useTranslate,
} from "react-admin";
import {
  DvirFormAreaPointSeverity,
  DvirFormDto,
  ProtectedFieldLevel,
} from "../../../common/data/service-proxies";
import { useProtectedFieldAccessLevel } from "../../../common/hooks/useProtectedFieldAccessLevel";
import { ProtectedElement } from "../../common/field-permissions/ProtectedElement";
import { FormToolbar } from "../../common/form/FormToolbar";
import { FormTabsHandler } from "./FormTabsHandler";

interface Props {
  record?: Record;
}

const validateForm = (values: DvirFormDto) => {
  if (!values.areas) {
    return {};
  }

  const retValue: any = {
    areas: values.areas.map((x) => ({
      id: undefined,
      name: undefined,
      points: x?.points?.map((p) => ({
        id: undefined,
        name: undefined,
        severity: undefined,
      })),
    })),
  };

  for (let areaIdx = 0; areaIdx < values.areas.length; areaIdx++) {
    if (!values.areas[areaIdx]) {
      continue;
    }

    const currentArea = values.areas[areaIdx];
    const areaName = currentArea.name;
    if (values.areas.filter((x) => !!x && x.name === areaName).length > 1) {
      retValue.areas[areaIdx].name =
        "resources.admin-dvir-forms.labels.duplicateName";
    }

    if (!currentArea.points) {
      continue;
    }

    for (let pointIdx = 0; pointIdx < currentArea.points.length; pointIdx++) {
      if (!currentArea.points[pointIdx]) {
        continue;
      }

      const pointName = currentArea.points[pointIdx].name;
      if (
        currentArea.points.filter((x) => !!x && x.name === pointName).length > 1
      ) {
        retValue.areas[areaIdx].points[pointIdx].name =
          "resources.admin-dvir-forms.labels.duplicateName";
      }
    }
  }

  return retValue;
};

export const DvirFormsForm = (props: Props) => {
  const translate = useTranslate();
  const [formTabs, setFormTabs] = useState<Array<string>>();
  const areasAccessLevel = useProtectedFieldAccessLevel("Areas");
  const severities: Array<{
    id: DvirFormAreaPointSeverity;
    name: string;
  }> = useMemo(
    () => [
      {
        id: DvirFormAreaPointSeverity.Minor,
        name: translate("resources.admin-dvir-forms.severities.minor", {
          _: "minor",
        }),
      },
      {
        id: DvirFormAreaPointSeverity.Major,
        name: translate("resources.admin-dvir-forms.severities.major", {
          _: "major",
        }),
      },
    ],
    [translate]
  );

  return (
    <TabbedForm
      className="form-create-edit"
      variant="outlined"
      validate={validateForm}
      toolbar={
        <FormToolbar
          showDeleteButton={!props.record || !props.record.isDefault}
        />
      }
      {...props}>
      <FormTab label="resources.admin-dvir-forms.labels.dvirForm">
        <ProtectedElement source="name">
          <TextInput
            source="name"
            label="resources.admin-dvir-forms.labels.name"
            validate={required()}
          />
        </ProtectedElement>
        <ProtectedElement source="isDefault">
          <BooleanInput
            source="isDefault"
            label="resources.admin-dvir-forms.labels.isDefault"
          />
        </ProtectedElement>
        <ArrayInput
          source="areas"
          label="resources.admin-dvir-forms.labels.areas">
          <SimpleFormIterator
            disableAdd={areasAccessLevel === ProtectedFieldLevel.ReadOnly}
            disableRemove={areasAccessLevel === ProtectedFieldLevel.ReadOnly}
            disableReordering={
              areasAccessLevel === ProtectedFieldLevel.ReadOnly
            }>
            <TextInput
              source="name"
              label="resources.admin-dvir-forms.labels.areaName"
              validate={required()}
              disabled={areasAccessLevel === ProtectedFieldLevel.ReadOnly}
            />
          </SimpleFormIterator>
        </ArrayInput>
      </FormTab>
      {formTabs &&
        formTabs?.map((x, i) => {
          const getSource = (source: string) => `areas[${i}].${source}`;
          return (
            x && (
              <FormTab key={i} label={x}>
                <ArrayInput
                  source={getSource("points")}
                  label="resources.admin-dvir-forms.labels.areaPoints">
                  <SimpleFormIterator
                    disableAdd={
                      areasAccessLevel === ProtectedFieldLevel.ReadOnly
                    }
                    disableRemove={
                      areasAccessLevel === ProtectedFieldLevel.ReadOnly
                    }
                    disableReordering={
                      areasAccessLevel === ProtectedFieldLevel.ReadOnly
                    }>
                    <TextInput
                      source="name"
                      label="resources.admin-dvir-forms.labels.pointName"
                      validate={required()}
                      disabled={
                        areasAccessLevel === ProtectedFieldLevel.ReadOnly
                      }
                    />
                    <SelectInput
                      source="severity"
                      label="resources.admin-dvir-forms.labels.severity"
                      choices={severities}
                      validate={required()}
                      disabled={
                        areasAccessLevel === ProtectedFieldLevel.ReadOnly
                      }
                    />
                  </SimpleFormIterator>
                </ArrayInput>
              </FormTab>
            )
          );
        })}
      <FormDataConsumer>
        {({ formData }) => (
          <FormTabsHandler
            formData={formData}
            formTabs={formTabs}
            setFormTabs={(tabs) => setFormTabs(tabs)}
          />
        )}
      </FormDataConsumer>
    </TabbedForm>
  );
};
